/**
 * My work — the saved-document list.
 *
 * Deliberately restrained: this is a drawer over the writing surface, not a file
 * manager. Delete is confirmed and then undoable, because deleting the wrong
 * page of handwriting is not something an apology fixes.
 */

import { useCallback, useEffect, useState } from 'react';

import { repositories } from '../storage/repositories';
import { createDocument, UNTITLED } from '../storage/documents';
import { bumped, type StoredDocument } from '../storage/types';
import { Button } from '../design/Button';

export interface MyWorkProps {
  activeId: string | null;
  onOpen: (id: string) => void;
  onNew: () => void;
  onClose: () => void;
}

export function MyWork({ activeId, onOpen, onNew, onClose }: MyWorkProps) {
  const [documents, setDocuments] = useState<StoredDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState<string | null>(null);
  /** The last deletion, kept just long enough to offer it back. */
  const [undoable, setUndoable] = useState<StoredDocument | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');

  const refresh = useCallback(async () => {
    const rows = await repositories().documents.list();
    setDocuments(rows);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const remove = async (id: string) => {
    const doomed = documents.find((d) => d.id === id) ?? null;
    await repositories().documents.remove(id);
    setConfirming(null);
    setUndoable(doomed);
    await refresh();
  };

  const undo = async () => {
    if (!undoable) return;
    await repositories().documents.restore(undoable.id);
    setUndoable(null);
    await refresh();
  };

  const duplicate = async (source: StoredDocument) => {
    const repos = repositories();
    const copy = createDocument(
      source.feature,
      // Structured clone rather than a shared reference: editing the copy must
      // not reach back into the original's ink.
      JSON.parse(JSON.stringify(source.payload)),
      `${source.title} copy`,
    );
    await repos.documents.put(copy);
    await refresh();
  };

  const commitRename = async (id: string) => {
    const repos = repositories();
    const existing = await repos.documents.get(id);
    if (existing) {
      await repos.documents.put({
        ...bumped(existing),
        title: draftTitle.trim() || UNTITLED,
      });
    }
    setRenaming(null);
    await refresh();
  };

  return (
    <aside className="my-work" aria-label="My work">
      <header className="my-work-head">
        <h3>My work</h3>
        <div className="my-work-actions">
          <Button variant="tertiary" icon="plus" onClick={onNew}>
            New
          </Button>
          <Button variant="tertiary" icon="close" onClick={onClose}>
            Close
          </Button>
        </div>
      </header>

      {undoable ? (
        <p className="my-work-undo" role="status">
          Deleted “{undoable.title}”.{' '}
          <Button variant="tertiary" onClick={undo}>
            Undo
          </Button>
        </p>
      ) : null}

      {loading ? (
        <p className="empty">Looking…</p>
      ) : documents.length === 0 ? (
        <p className="empty">Nothing saved yet. What you write is kept automatically.</p>
      ) : (
        <ul className="my-work-list">
          {documents.map((document) => (
            <li key={document.id} className={document.id === activeId ? 'is-active' : ''}>
              {renaming === document.id ? (
                <form
                  className="my-work-rename"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void commitRename(document.id);
                  }}
                >
                  <label className="sr-only" htmlFor={`rename-${document.id}`}>
                    Title
                  </label>
                  <input
                    className="mm-input"
                    id={`rename-${document.id}`}
                    value={draftTitle}
                    autoFocus
                    onChange={(event) => setDraftTitle(event.target.value)}
                    onBlur={() => void commitRename(document.id)}
                  />
                </form>
              ) : (
                <button
                  type="button"
                  className="my-work-open"
                  onClick={() => onOpen(document.id)}
                  aria-current={document.id === activeId ? 'true' : undefined}
                >
                  <span className="my-work-title">{document.title}</span>
                  <span className="my-work-meta">
                    {document.feature} · {relativeTime(document.updatedAt)}
                  </span>
                </button>
              )}

              <div className="my-work-row-actions">
                <Button
                  variant="tertiary"
                  onClick={() => {
                    setRenaming(document.id);
                    setDraftTitle(document.title);
                  }}
                >
                  Rename
                </Button>
                <Button variant="tertiary" onClick={() => void duplicate(document)}>
                  Duplicate
                </Button>
                {confirming === document.id ? (
                  <>
                    <Button variant="destructive" onClick={() => void remove(document.id)}>
                      Really delete
                    </Button>
                    <Button variant="tertiary" onClick={() => setConfirming(null)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button variant="tertiary" onClick={() => setConfirming(document.id)}>
                    Delete
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

/** "3 minutes ago" reads better than a timestamp on a page you just left. */
export function relativeTime(when: number, now = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - when) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(when).toLocaleDateString();
}
